import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Pencil, Plus, Trash2, AlertCircle, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import catalogService from '@/services/catalogService.ts';
import { useCatalog, invalidateCatalog } from '@/hooks/useCatalog.ts';
import { useCreateCatalogDraft } from '@/hooks/useCatalogEditDraft.ts';
import type { CatalogEntry, CatalogKind, CatalogEditDraft } from '@/types/catalog.ts';
import CatalogEntryFormModal from './CatalogEntryFormModal.tsx';

interface Props {
  kind: CatalogKind;
  onDraftCreated: (draft: CatalogEditDraft) => void;
}

const CREATE_DISABLED: CatalogKind[] = ['Difficulty'];
const DELETE_DISABLED: CatalogKind[] = ['Difficulty'];

export default function CatalogList({ kind, onDraftCreated }: Props) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage ?? 'es').toLowerCase();
  const { data: active, loading, error, refetch } = useCatalog(kind);
  const [showInactive, setShowInactive] = useState(false);
  const [inactive, setInactive] = useState<CatalogEntry[]>([]);
  const [inactiveLoading, setInactiveLoading] = useState(false);
  const [editing, setEditing] = useState<CatalogEntry | null>(null);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const { create } = useCreateCatalogDraft();

  const fetchInactive = useCallback(async () => {
    setInactiveLoading(true);
    try {
      setInactive(await catalogService.getInactiveByKind(kind));
    } catch {
      // silent
    } finally {
      setInactiveLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    if (showInactive) fetchInactive();
  }, [showInactive, fetchInactive]);

  useEffect(() => {
    setShowInactive(false);
    setInactive([]);
  }, [kind]);

  const rows: CatalogEntry[] = useMemo(() => {
    if (!showInactive) return active ?? [];
    return inactive;
  }, [showInactive, active, inactive]);

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => a.sortOrder - b.sortOrder || a.slug.localeCompare(b.slug));
  }, [rows]);

  async function handleSoftDelete(entry: CatalogEntry) {
    if (DELETE_DISABLED.includes(kind)) return;
    if (!window.confirm(t('superuser.catalog.confirmDelete', {
      slug: entry.slug,
      defaultValue: 'Crear borrador para desactivar «{{slug}}»?',
    }))) return;

    setDeletingId(entry.id);
    try {
      const draft = await create(kind, {
        entryId: entry.id,
        operation: 'Delete',
        language: lang,
        content: {
          slug: entry.slug,
          parentSlug: entry.parentSlug ?? null,
          label: entry.labels[lang] ?? entry.labels.es ?? entry.slug,
          briefing: entry.briefings[lang] ?? entry.briefings.es ?? null,
        },
      });
      toast.success(t('superuser.catalog.toast.draftSaved', {
        defaultValue: 'Cambio guardado, traducción en curso. Te notificaremos.',
      }));
      onDraftCreated(draft);
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'ERROR';
      toast.error(t(`superuser.catalog.errors.${raw}`, { defaultValue: raw }));
    } finally {
      setDeletingId(null);
    }
  }

  async function handleRestore(entry: CatalogEntry) {
    setRestoringId(entry.id);
    try {
      await catalogService.restoreEntry(entry.id);
      toast.success(t('superuser.catalog.toast.restored', {
        defaultValue: 'Entrada activada.',
      }));
      invalidateCatalog(kind);
      refetch();
      // Re-fetch lista de inactivos para que el botón [Activar] desaparezca de esta entry
      if (showInactive) await fetchInactive();
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'ERROR';
      toast.error(t(`superuser.catalog.errors.${raw}`, { defaultValue: raw }));
    } finally {
      setRestoringId(null);
    }
  }

  function handleSavedDraft(draft: CatalogEditDraft) {
    setEditing(null);
    setCreating(false);
    invalidateCatalog(kind);
    refetch();
    onDraftCreated(draft);
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="size-4 rounded border-border"
          />
          {t('superuser.catalog.showInactive', { defaultValue: 'Mostrar inactivos' })}
        </label>

        {!CREATE_DISABLED.includes(kind) && (
          <Button onClick={() => setCreating(true)}>
            <Plus className="size-4" />
            {t('superuser.catalog.newEntry', { defaultValue: 'Nueva entrada' })}
          </Button>
        )}
      </div>

      {(loading || inactiveLoading) && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          {t('common.loading', { defaultValue: 'Cargando…' })}
        </div>
      )}

      {error && !loading && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && sorted.length === 0 && (
        <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
          {showInactive
            ? t('superuser.catalog.emptyInactive', { defaultValue: 'No hay entradas inactivas.' })
            : t('superuser.catalog.empty', { defaultValue: 'No hay entradas.' })}
        </div>
      )}

      {sorted.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Slug</th>
                <th className="px-4 py-3 text-left font-medium">
                  {t('superuser.catalog.col.label', { defaultValue: 'Etiqueta' })}
                </th>
                {kind === 'SubSector' && (
                  <th className="px-4 py-3 text-left font-medium">
                    {t('superuser.catalog.col.parentSlug', { defaultValue: 'Sector padre' })}
                  </th>
                )}
                <th className="px-4 py-3 text-left font-medium">
                  {t('superuser.catalog.col.active', { defaultValue: 'Activo' })}
                </th>
                <th className="px-4 py-3 text-right font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sorted.map((entry) => (
                <tr key={entry.id} className="text-foreground">
                  <td className="px-4 py-3 font-mono text-xs">{entry.slug}</td>
                  <td className="px-4 py-3">
                    {entry.labels[lang] ?? entry.labels.es ?? <span className="text-muted-foreground">—</span>}
                  </td>
                  {kind === 'SubSector' && (
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{entry.parentSlug ?? '—'}</td>
                  )}
                  <td className="px-4 py-3">
                    {entry.isActive ? (
                      <span className="inline-flex rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                        {t('common.yes', { defaultValue: 'Sí' })}
                      </span>
                    ) : (
                      <span className="inline-flex rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                        {t('common.no', { defaultValue: 'No' })}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button variant="ghost" size="xs" onClick={() => setEditing(entry)}>
                        <Pencil className="size-3" />
                        {t('superuser.catalog.edit', { defaultValue: 'Editar' })}
                      </Button>
                      {entry.isActive && !DELETE_DISABLED.includes(kind) && (
                        <Button
                          variant="ghost"
                          size="xs"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleSoftDelete(entry)}
                          disabled={deletingId === entry.id}
                        >
                          {deletingId === entry.id ? (
                            <Loader2 className="size-3 animate-spin" />
                          ) : (
                            <Trash2 className="size-3" />
                          )}
                          {t('superuser.catalog.deactivate', { defaultValue: 'Desactivar' })}
                        </Button>
                      )}
                      {!entry.isActive && (
                        <Button
                          variant="ghost"
                          size="xs"
                          className="text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300"
                          onClick={() => handleRestore(entry)}
                          disabled={restoringId === entry.id}
                        >
                          {restoringId === entry.id ? (
                            <Loader2 className="size-3 animate-spin" />
                          ) : (
                            <RotateCcw className="size-3" />
                          )}
                          {t('superuser.catalog.activate', { defaultValue: 'Activar' })}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CatalogEntryFormModal
        isOpen={creating || editing !== null}
        kind={kind}
        entry={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={handleSavedDraft}
      />
    </div>
  );
}
